export function toEpochMs(t) {
  if (t == null) return null;
  if (typeof t === "number" && Number.isFinite(t)) return t;
  const d = new Date(t);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function lerp(a, b, t) {
  return a + (b - a) * t;
}

function clamp01(x) {
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(1, x));
}

/**
 * Map MSL height to RGB (red -> green -> blue).
 * Returns CSS rgb() string.
 */
export function mslToRgb(msl, minMsl, maxMsl) {
  const denom = (maxMsl ?? 0) - (minMsl ?? 0);
  const t = denom === 0 ? 0.5 : (msl - minMsl) / denom;
  const u = clamp01(t);

  // piecewise: red(1,0,0) -> green(0,1,0) -> blue(0,0,1)
  let r, g, b;
  if (u <= 0.5) {
    const k = u / 0.5;
    r = lerp(255, 0, k);
    g = lerp(0, 255, k);
    b = 0;
  } else {
    const k = (u - 0.5) / 0.5;
    r = 0;
    g = lerp(255, 0, k);
    b = lerp(0, 255, k);
  }
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}

/** AGL color stops: red 150 m → yellow 300 m → green 600 m → blue 1000 m → purple 1500 m → white 2500 m. */
export const AGL_COLOR_STOPS = [
  { m: 0, r: 255, g: 0, b: 0 },
  { m: 150, r: 255, g: 0, b: 0 },
  { m: 300, r: 255, g: 255, b: 0 },
  { m: 600, r: 0, g: 255, b: 0 },
  { m: 1000, r: 0, g: 0, b: 255 },
  { m: 1500, r: 160, g: 32, b: 240 },
  { m: 2500, r: 255, g: 255, b: 255 },
];

/** Resolve AGL in meters from track point fields. */
export function effectiveAgl(point) {
  if (!point) return null;
  const agl = Number(point.agl);
  if (Number.isFinite(agl) && agl >= 0) return agl;
  const msl = Number(point.msl);
  const terrain = Number(point.terrain);
  if (Number.isFinite(msl) && Number.isFinite(terrain)) {
    const computed = msl - terrain;
    if (computed >= 0) return computed;
  }
  return null;
}

function interpolateAglStops(agl, stops = AGL_COLOR_STOPS) {
  if (!Number.isFinite(agl) || !stops?.length) {
    return { r: 128, g: 128, b: 128 };
  }
  if (agl <= stops[0].m) {
    return { r: stops[0].r, g: stops[0].g, b: stops[0].b };
  }
  const last = stops[stops.length - 1];
  if (agl >= last.m) {
    return { r: last.r, g: last.g, b: last.b };
  }
  for (let i = 1; i < stops.length; i++) {
    const hi = stops[i];
    const lo = stops[i - 1];
    if (agl <= hi.m) {
      const span = hi.m - lo.m;
      const k = span <= 0 ? 1 : clamp01((agl - lo.m) / span);
      return {
        r: lerp(lo.r, hi.r, k),
        g: lerp(lo.g, hi.g, k),
        b: lerp(lo.b, hi.b, k),
      };
    }
  }
  return { r: last.r, g: last.g, b: last.b };
}

/**
 * Map AGL (m) to RGB: red→150 m, yellow→300 m, green→600 m, blue→1000 m, purple→1500 m, white→2500 m.
 * Returns CSS rgb() string.
 */
export function aglToRgb(agl, stops = AGL_COLOR_STOPS) {
  if (!Number.isFinite(agl)) return "rgb(128, 128, 128)";
  const { r, g, b } = interpolateAglStops(agl, stops);
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}

/** How many sub-segments to draw along one map edge (smooth AGL color gradient). */
export const TRACK_EDGE_SUB_STEPS = 16;

/** Sub-segment count from AGL change along edge (~35 m per step, capped). */
export function aglSubStepsForEdge(a, b, maxSteps = TRACK_EDGE_SUB_STEPS) {
  const aglA = effectiveAgl(a);
  const aglB = effectiveAgl(b);
  if (!Number.isFinite(aglA) && !Number.isFinite(aglB)) return 1;
  const agl0 = Number.isFinite(aglA) ? aglA : aglB;
  const agl1 = Number.isFinite(aglB) ? aglB : aglA;
  const delta = Math.abs(agl1 - agl0);
  return Math.max(1, Math.min(maxSteps, Math.ceil(delta / 35)));
}

/**
 * Split edge A→B into short polylines with AGL-interpolated color (smooth transitions on map).
 * @returns {{ positions: [number, number][], color: string }[]}
 */
export function trackEdgeSubsegments(a, b, minMsl, maxMsl, maxSteps = TRACK_EDGE_SUB_STEPS) {
  const steps = aglSubStepsForEdge(a, b, maxSteps);
  const aglA = effectiveAgl(a);
  const aglB = effectiveAgl(b);
  const useAgl = Number.isFinite(aglA) || Number.isFinite(aglB);
  const agl0 = Number.isFinite(aglA) ? aglA : aglB;
  const agl1 = Number.isFinite(aglB) ? aglB : aglA;
  const mslA = Number.isFinite(a?.msl) ? a.msl : minMsl;
  const mslB = Number.isFinite(b?.msl) ? b.msl : minMsl;
  const out = [];

  for (let s = 0; s < steps; s++) {
    const t0 = s / steps;
    const t1 = (s + 1) / steps;
    const tMid = (t0 + t1) / 2;
    const lat0 = lerp(a.lat, b.lat, t0);
    const lon0 = lerp(a.lon, b.lon, t0);
    const lat1 = lerp(a.lat, b.lat, t1);
    const lon1 = lerp(a.lon, b.lon, t1);
    const color = useAgl
      ? aglToRgb(lerp(agl0, agl1, tMid))
      : mslToRgb(lerp(mslA, mslB, tMid), minMsl, maxMsl);
    out.push({
      positions: [
        [lat0, lon0],
        [lat1, lon1],
      ],
      color,
    });
  }
  return out;
}

/** Color map segment by average AGL, falling back to MSL scale when AGL is unknown. */
export function trackSegmentColor(a, b, minMsl, maxMsl) {
  const aglA = effectiveAgl(a);
  const aglB = effectiveAgl(b);
  if (Number.isFinite(aglA) || Number.isFinite(aglB)) {
    const avgAgl =
      Number.isFinite(aglA) && Number.isFinite(aglB)
        ? (aglA + aglB) / 2
        : Number.isFinite(aglA)
          ? aglA
          : aglB;
    return aglToRgb(avgAgl);
  }
  const mslA = Number.isFinite(a?.msl) ? a.msl : minMsl;
  const mslB = Number.isFinite(b?.msl) ? b.msl : minMsl;
  return mslToRgb((mslA + mslB) / 2, minMsl, maxMsl);
}

/**
 * Split points into segments when there is a gap dt > gapThresholdMs.
 * Points must have time (either `t`, `time`, `ts`, `timestamp`) parsable by toEpochMs.
 */
export function splitTrackByGap(points, gapThresholdMs = 120000) {
  const res = [];
  if (!Array.isArray(points) || points.length === 0) return res;

  const getTime = (p) =>
    toEpochMs(p?.tMs ?? p?.t ?? p?.time ?? p?.ts ?? p?.timestamp ?? p?.dateTime);

  let cur = [points[0]];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const next = points[i];
    const dt = (getTime(next) ?? 0) - (getTime(prev) ?? 0);
    if (Number.isFinite(dt) && dt > gapThresholdMs) {
      res.push({ points: cur, hasGapAfter: true });
      cur = [next];
    } else {
      cur.push(next);
    }
  }
  res.push({ points: cur, hasGapAfter: false });
  return res;
}

/** Straight line across a recording gap (last point of segment → first of next). */
export function gapBridgePositions(segmentPoints, nextSegmentPoints) {
  if (!segmentPoints?.length || !nextSegmentPoints?.length) return null;
  const a = segmentPoints[segmentPoints.length - 1];
  const b = nextSegmentPoints[0];
  if (!Number.isFinite(a?.lat) || !Number.isFinite(a?.lon)) return null;
  if (!Number.isFinite(b?.lat) || !Number.isFinite(b?.lon)) return null;
  return [
    [a.lat, a.lon],
    [b.lat, b.lon],
  ];
}

/** Uniform decimation for client-side display (keeps first/last). */
export function decimateTrack(points, maxPoints) {
  if (!Array.isArray(points) || points.length === 0) return [];
  if (points.length <= maxPoints) return points;
  const out = [points[0]];
  const lastIdx = points.length - 1;
  if (maxPoints <= 2) {
    return [points[0], points[lastIdx]];
  }
  const step = lastIdx / (maxPoints - 1);
  let prev = 0;
  for (let k = 1; k < maxPoints - 1; k++) {
    let idx = Math.round(k * step);
    if (idx <= prev) idx = prev + 1;
    if (idx >= lastIdx) break;
    out.push(points[idx]);
    prev = idx;
  }
  const last = points[lastIdx];
  if (out[out.length - 1] !== last) out.push(last);
  return out;
}

/** Binary search nearest point by tMs (points sorted by tMs). */
export function findNearestIndexByTMs(points, tMs) {
  if (!points?.length || !Number.isFinite(tMs)) return 0;
  if (points.length === 1) return 0;
  let lo = 0;
  let hi = points.length - 1;
  if (tMs <= (points[0].tMs ?? 0)) return 0;
  if (tMs >= (points[hi].tMs ?? 0)) return hi;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    const t = points[mid].tMs ?? 0;
    if (tMs < t) hi = mid;
    else lo = mid;
  }
  const dLo = Math.abs((points[lo].tMs ?? 0) - tMs);
  const dHi = Math.abs((points[hi].tMs ?? 0) - tMs);
  return dLo <= dHi ? lo : hi;
}

export function parseIgc(text) {
  const lines = String(text ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  let gliderId = null;
  const points = [];

  for (const line of lines) {
    if (line.startsWith("H")) {
      // minimal: try to extract any known ID fields (HFGID, HFPLTPILOTINCHARGE, etc.)
      if (line.startsWith("HFGID")) {
        gliderId = line.slice(5).replace(/^[:\s]*/, "").trim() || gliderId;
      }
      continue;
    }
    if (!line.startsWith("B") || line.length < 35) continue;

    // BHHMMSSDDMMmmmNDDDMMmmmEAxxxxGyyyyy (altitude parsing can vary)
    const hh = Number(line.slice(1, 3));
    const mm = Number(line.slice(3, 5));
    const ss = Number(line.slice(5, 7));
    if (![hh, mm, ss].every(Number.isFinite)) continue;
    const timeSec = hh * 3600 + mm * 60 + ss;

    const latDeg = Number(line.slice(7, 9));
    const latMinTh = Number(line.slice(9, 14)); // minutes * 1000
    const latHem = line.slice(14, 15);

    const lonDeg = Number(line.slice(15, 18));
    const lonMinTh = Number(line.slice(18, 23));
    const lonHem = line.slice(23, 24);

    if (![latDeg, latMinTh, lonDeg, lonMinTh].every(Number.isFinite)) continue;

    const lat = latDeg + latMinTh / 60000;
    const lon = lonDeg + lonMinTh / 60000;
    const latSigned = latHem === "S" ? -lat : lat;
    const lonSigned = lonHem === "W" ? -lon : lon;

    // pressure alt (xxxx) and gps alt (yyyyy) - take GPS alt when present
    const pAlt = Number(line.slice(25, 30));
    const gAlt = Number(line.slice(30, 35));
    const msl = Number.isFinite(gAlt) ? gAlt : (Number.isFinite(pAlt) ? pAlt : null);

    points.push({ timeSec, lat: latSigned, lon: lonSigned, msl });
  }

  return { gliderId, points };
}

